import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pin, Megaphone, X, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import { toast } from "sonner";
import { PageHeader, Button, Badge, SearchInput, EmptyState, Card } from "@/components/ui-em";

export default function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", body_html: "", category: "General", pinned: false, priority: "normal" });

  const load = () => {
    setLoading(true);
    api.get("/announcements", { params: { raw: 1 } }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = items.filter((a) => (!q ? true : a.title.toLowerCase().includes(q.toLowerCase())));

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/announcements", form);
      toast.success("Posted");
      setShowForm(false);
      setForm({ title: "", body_html: "", category: "General", pinned: false, priority: "normal" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    try {
      await api.delete(`/announcements/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Admin · Content"
        title="Announcements"
        description="Broadcast updates, pin critical news, keep everyone aligned."
        actions={<Button icon={Plus} onClick={() => setShowForm(true)} size="lg" data-testid="admin-new-announcement">New announcement</Button>}
      />

      <SearchInput value={q} onChange={setQ} placeholder="Search announcements..." />

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-zinc-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Megaphone}
            title="No announcements yet"
            description="Keep your team informed with pinned or important announcements."
            actionLabel="Post first announcement"
            onAction={() => setShowForm(true)}
            className="m-4"
          />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold text-left">
                <th className="px-6 py-4">Title</th>
                <th className="px-6 py-4">Category</th>
                <th className="px-6 py-4">Priority</th>
                <th className="px-6 py-4">Author</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} className="border-t border-white/5 hover:bg-white/[0.02]" style={{ transitionProperty: "background-color", transitionDuration: "140ms" }}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {a.pinned && <Pin className="w-3.5 h-3.5 text-gold" />}
                      <span className="text-white">{a.title}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-xs text-gold">{a.category}</td>
                  <td className="px-6 py-4">
                    <Badge variant={a.priority === "important" ? "danger" : "outline"} icon={a.priority === "important" ? AlertTriangle : undefined} uppercase>
                      {a.priority || "normal"}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-xs text-zinc-400">{a.author}</td>
                  <td className="px-6 py-4 text-xs text-zinc-500">{new Date(a.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => remove(a.id)}
                      className="p-2 rounded-md text-zinc-500 hover:text-red-300 hover:bg-red-500/10"
                      style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {showForm && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-ink-0/70 backdrop-blur-md overflow-y-auto">
          <div className="em-card p-8 w-full max-w-2xl my-8" style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7)" }}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display text-xl">New announcement</h3>
              <button onClick={() => setShowForm(false)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="em-input" data-testid="ann-title" />
              <div className="grid grid-cols-3 gap-3">
                <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="em-input" />
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="em-input" data-testid="ann-priority">
                  <option value="normal">Normal</option>
                  <option value="important">Important</option>
                </select>
                <label className="flex items-center gap-2 text-sm text-zinc-400 px-3 em-input cursor-pointer">
                  <input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} className="accent-gold w-4 h-4" />
                  <span>Pinned</span>
                </label>
              </div>
              <RichTextEditor value={form.body_html} onChange={(h) => setForm({ ...form, body_html: h })} placeholder="Write the announcement..." />
              <Button type="submit" variant="primary" size="lg" className="w-full" data-testid="ann-submit">Post announcement</Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
